/**
 * M3X.TRAVEL frontend pages (PR 34-35).
 *
 * Per BUILD_READINESS_M3X_TRAVEL_FULL.md §1.K + §1.B.2 + §1.A.3:
 *
 *   - `/me/travel`                          — Employee Travel Hub
 *   - `/operations/transactions/travel`     — HR card-based list
 *   - `/policies/travel-per-diem`           — Per-LE catalog editor
 *   - `/reports/travel`                     — Reports library
 *
 * Each page is a scaffold that consumes the backend REST surface
 * shipped in PRs 1-32. Full Solvait-modal request-builder UI lands
 * in a polish follow-up — this PR ensures every route is reachable
 * (Commandment 71's LIVE-REACHABLE bar).
 */

import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  CalendarRange,
  CreditCard,
  Plane,
  PlusCircle,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Loading } from "../../components/Loading";
import {
  EmptyState,
  PageHeader,
  SettingsEntryCard,
  SettingsSection,
} from "../../components/ui";
import { useLegalEntityId } from "../../lib/legalEntity";


// ===========================================================================
// /me/travel — Employee Travel Hub
// ===========================================================================

type TravelRequestDTO = {
  id: string;
  request_number: number | null;
  workflow_state: string;
  travel_type: string;
  purpose: string | null;
  start_date: string | null;
  end_date: string | null;
  duration_days: number | null;
  total_amount: string | null;
  advance_amount: string | null;
  currency: string;
};

function _statePillClass(state: string): string {
  const map: Record<string, string> = {
    draft: "bg-surface-sunken text-text-tertiary border-border-subtle",
    submitted: "bg-amber-50 text-amber-700 border-amber-200",
    pending_approval: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    booked: "bg-sky-50 text-sky-700 border-sky-200",
    travelling: "bg-violet-50 text-violet-700 border-violet-200",
    returned: "bg-sky-50 text-sky-700 border-sky-200",
    reconciled: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-surface-sunken text-text-tertiary border-border-subtle",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    cancelled: "bg-rose-50 text-rose-700 border-rose-200",
    change_requested: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return map[state] ?? map.draft;
}


export function MyTravelHubPage() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("travel.my_hub_title", "My Business Travel")}
        subtitle={t(
          "travel.my_hub_subtitle",
          "Request a trip, track approvals, and reconcile per diem when you return.",
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={Plane}
          label={t("travel.stat_trips_ytd", "Trips this year")}
          value="—"
        />
        <StatTile
          icon={CreditCard}
          label={t("travel.stat_per_diem_ytd", "Per diem YTD")}
          value="—"
        />
        <StatTile
          icon={CalendarRange}
          label={t("travel.stat_pending", "Pending approval")}
          value="—"
        />
        <StatTile
          icon={Briefcase}
          label={t("travel.stat_upcoming", "Upcoming trips")}
          value="—"
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border border-border-default bg-surface-default p-4">
        <div>
          <h2 className="text-base font-semibold text-text-primary">
            {t("travel.cta_request_title", "Request a Business Travel")}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            {t(
              "travel.cta_request_subtitle",
              "Open the 5-section request modal — destination, purpose, itinerary, documents, submit.",
            )}
          </p>
        </div>
        <Link
          to="/me/travel/new"
          className="inline-flex items-center gap-2 rounded-md bg-brand-deep px-4 py-2 text-sm font-medium text-white hover:bg-brand-blue"
        >
          <PlusCircle className="h-4 w-4" />
          {t("travel.cta_request_button", "Request a Business Travel")}
        </Link>
      </div>

      <RecentTravelList employeeOnly />
    </div>
  );
}


function StatTile({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Plane;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-default p-4">
      <div className="flex items-center gap-2 text-text-tertiary">
        <Icon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-text-primary">
        {value}
      </div>
    </div>
  );
}


function RecentTravelList({ employeeOnly }: { employeeOnly: boolean }) {
  const { t } = useTranslation();
  const legalEntityId = useLegalEntityId();
  const { data, isLoading } = useQuery({
    queryKey: ["travel", "requests", legalEntityId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (legalEntityId) params.set("legal_entity_id", legalEntityId);
      const url = `/api/travel/requests?${params}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) return [] as TravelRequestDTO[];
      return (await resp.json()) as TravelRequestDTO[];
    },
  });

  if (isLoading) return <Loading />;
  const items = data ?? [];

  if (items.length === 0) {
    return (
      <EmptyState
        icon={Plane}
        headline={t("travel.empty_headline", "No travel requests yet")}
        description={t(
          "travel.empty_desc",
          "Click \"Request a Business Travel\" to start your first trip request.",
        )}
      />
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-text-primary">
        {employeeOnly
          ? t("travel.my_recent", "Your recent travel")
          : t("travel.all_recent", "All travel requests")}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => (
          <div
            key={r.id}
            className="rounded-lg border border-border-default bg-surface-default p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium text-text-primary">
                TLR-{(r.request_number ?? 0).toString().padStart(6, "0")}
              </div>
              <span
                className={`rounded-full border px-2 py-0.5 text-xs ${_statePillClass(r.workflow_state)}`}
              >
                {r.workflow_state.replace(/_/g, " ")}
              </span>
            </div>
            <div className="mt-2 text-sm text-text-secondary">
              {r.travel_type === "international" ? "🌐" : "🇸🇦"}{" "}
              {r.purpose || "Business trip"}
            </div>
            <div className="mt-2 text-xs text-text-tertiary">
              {r.start_date} → {r.end_date} ({r.duration_days} days)
            </div>
            {r.advance_amount && (
              <div className="mt-2 text-xs text-text-secondary">
                {t("travel.advance", "Advance")}: {r.currency}{" "}
                {parseFloat(r.advance_amount).toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ===========================================================================
// /operations/transactions/travel — HR list view
// ===========================================================================


export function TravelRequestsHRPage() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("travel.hr_title", "Business Travel")}
        subtitle={t(
          "travel.hr_subtitle",
          "Approve / reject travel requests, track active trips, and review reconciliations.",
        )}
      />
      <RecentTravelList employeeOnly={false} />
    </div>
  );
}


// ===========================================================================
// /policies/travel-per-diem — Catalog editor (scaffold)
// ===========================================================================


export function TravelPerDiemEditorPage() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("travel.per_diem_editor_title", "Per Diem Catalog")}
        subtitle={t(
          "travel.per_diem_editor_subtitle",
          "Per-LE per-category per-destination per-day-range matrix. 4 default categories seeded: CEO / GMs & Directors / Managers / Supervisors. Add per-day-range tiers as needed.",
        )}
      />

      <SettingsSection
        title={t("travel.per_diem_categories", "Categories")}
      >
        <SettingsEntryCard
          icon={Sparkles}
          title={t("travel.cat_ceo", "CEO")}
          description={t(
            "travel.cat_ceo_desc",
            "Chief Executive Officer per diem schedule (top tier)",
          )}
          to="#"
          tone="purple"
        />
        <SettingsEntryCard
          icon={Briefcase}
          title={t("travel.cat_gms", "GMs & Directors")}
          description={t(
            "travel.cat_gms_desc",
            "General Managers & Directors per diem schedule",
          )}
          to="#"
          tone="indigo"
        />
        <SettingsEntryCard
          icon={TrendingUp}
          title={t("travel.cat_managers", "Managers")}
          description={t("travel.cat_managers_desc", "Managers per diem schedule")}
          to="#"
          tone="teal"
        />
        <SettingsEntryCard
          icon={CreditCard}
          title={t("travel.cat_supervisors", "Supervisors")}
          description={t(
            "travel.cat_supervisors_desc",
            "Supervisors per diem schedule",
          )}
          to="#"
          tone="neutral"
        />
      </SettingsSection>

      <div className="rounded-lg border border-border-default bg-surface-default p-4 text-sm text-text-secondary">
        <p>
          {t(
            "travel.per_diem_editor_note",
            "Full editor (inline per-day-range table, drag-drop tier reorder, M19 onboarding auto-flush preview) ships in a polish follow-up. Backend REST surface (`/api/travel/per-diem/*`) is operational today; HR can manage via API or wait for the UI.",
          )}
        </p>
      </div>
    </div>
  );
}


// ===========================================================================
// /reports/travel — Reports library
// ===========================================================================
//
// M3X.REPORTS hotfix (domain index consistency):
//   TravelReportsPage removed from this file. The /reports/travel route
//   now renders TravelReportsLandingPage via <ReportsDomainIndex> from
//   console/src/pages/reports/DomainIndexPages.tsx — same searchable
//   card-grid pattern as /reports/excuses.
//
//   The raw legacy stat-tile dump (TravelReportSection envelope from
//   GET /api/reports/travel) is dead code per spec; the manifests-driven
//   /reports/travel/{report_id} pages render each section via
//   <ConfigurableReportPage> instead.
